import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

/**
 * Custom hook for restaurant-aware navigation
 * Automatically prepends restaurant ID to all protected routes
 */
export const useRestaurantNav = () => {
  const navigate = useNavigate();
  const { restaurantId } = useParams<{ restaurantId: string }>();

  // Get restaurant ID from params or localStorage
  const currentRestaurantId = useMemo(() => {
    return restaurantId || localStorage.getItem('restaurantId') || '4bebbb8a-a2bf-4d53-b287-842c3c5be847';
  }, [restaurantId]);

  /**
   * Navigate with automatic restaurant ID prepending for protected routes
   * Auth routes (login, signup, etc.) are not modified
   */
  const navigateWithRestaurant = useCallback((
    path: string | number,
    options?: { replace?: boolean; state?: any }
  ) => {
    // If path is a number (for back navigation), just navigate normally
    if (typeof path === 'number') {
      navigate(path);
      return;
    }

    // Auth routes that should NOT have restaurant ID
    const authRoutes = ['/login', '/signup', '/forgot-password', '/clover', '/clover/callback', '/generate', '/processing'];
    
    // Check if it's an auth route
    const isAuthRoute = authRoutes.some(route => path.startsWith(route));
    
    // If it's already has restaurant ID or is auth route, navigate as is
    if (isAuthRoute || path.includes(currentRestaurantId)) {
      navigate(path, options);
    } else {
      // Prepend restaurant ID to the path
      const newPath = `/${currentRestaurantId}${path}`;
      navigate(newPath, options);
    }
  }, [navigate, currentRestaurantId]);

  return {
    navigate: navigateWithRestaurant,
    restaurantId: currentRestaurantId,
  };
};
